import React from 'react';
import { Mail, AlertTriangle } from 'lucide-react';

/** Fields the readiness check needs; a subset of a product line. */
export interface EmailReadinessInput {
  tagline?: string | null;
  description?: string | null;
  /** Active variants of the line. Counts arrive from pg as strings. */
  active_variant_count?: number | string;
  /** Active variants that have a packshot. */
  variant_image_count?: number | string;
}

function toCount(value: number | string | undefined): number | null {
  if (value === undefined || value === null) return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Gaps that would make the follow-up mail look unfinished, in the same order the send
 * dialog reports them. Returns null when the variant counts are unknown — a cached API
 * response must not produce a confident "ready".
 */
export function emailReadinessGaps(product: EmailReadinessInput): string[] | null {
  const activeVariants = toCount(product.active_variant_count);
  const withImage = toCount(product.variant_image_count);
  if (activeVariants === null || withImage === null) return null;

  const gaps: string[] = [];
  if (!product.tagline?.trim() && !product.description?.trim()) {
    gaps.push('copy');
  }
  // Without an active variant there is no price row and no image either, so naming both
  // would just repeat the same root cause.
  if (activeVariants === 0) {
    gaps.push('pack size');
  } else if (withImage === 0) {
    gaps.push('packshot');
  }
  return gaps;
}

const BASE_CLASS =
  'inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-black uppercase ' +
  'tracking-tight whitespace-nowrap';

/** Shows at a glance whether a product line can be sent to a PoS after a visit. */
export const EmailReadyBadge: React.FC<{ product: EmailReadinessInput }> = ({
  product,
}) => {
  const gaps = emailReadinessGaps(product);
  if (gaps === null) return null;

  if (gaps.length === 0) {
    return (
      <span
        className={`${BASE_CLASS} text-green-700 bg-green-50`}
        title="Tagline or description, an active pack size and a packshot are present."
      >
        <Mail size={11} /> Mail ready
      </span>
    );
  }

  return (
    <span
      className={`${BASE_CLASS} text-amber-700 bg-amber-50`}
      title="Missing content is listed as a warning before the follow-up mail is sent."
    >
      <AlertTriangle size={11} /> No {gaps.join(', ')}
    </span>
  );
};
